/**
 * Gestion du credential d'authentification côté client.
 *
 * Un seul credential est stocké dans localStorage sous `calibre_rag_api_key` :
 * - en mode JWT c'est le token renvoyé par /api/auth/login ;
 * - en mode clé API c'est la clé statique AUTH_API_KEY.
 * Dans les deux cas il est envoyé en `Authorization: Bearer <cred>` (+ `X-API-Key`
 * pour compat clé API), ce qui laisse les clients API existants inchangés.
 */
export const AUTH_STORAGE_KEY = 'calibre_rag_api_key';

/** Nom de l'événement global émis sur un 401 (session expirée / non authentifié). */
export const UNAUTHORIZED_EVENT = 'biblio:unauthorized';

function hasLocalStorage() {
  return typeof localStorage !== 'undefined' && localStorage !== null;
}

export function getAuthToken() {
  return hasLocalStorage() ? localStorage.getItem(AUTH_STORAGE_KEY) : null;
}

export function setAuthToken(token) {
  if (!hasLocalStorage()) return;
  if (token) localStorage.setItem(AUTH_STORAGE_KEY, token);
  else localStorage.removeItem(AUTH_STORAGE_KEY);
}

/**
 * Jeton de RENOUVELLEMENT (mode Supabase uniquement).
 *
 * Le jeton d'accès Supabase vit une heure. Sans celui-ci, l'application tombe
 * entièrement en 401 au bout de ce délai — constaté en production le
 * 2026-09-10. Il est stocké à part du jeton d'accès parce qu'il a une durée de
 * vie et un usage différents : il ne part JAMAIS dans un en-tête `Authorization`,
 * uniquement dans le corps de `POST /api/auth/refresh`.
 */
export const REFRESH_STORAGE_KEY = 'calibre_rag_refresh_token';

export function getRefreshToken() {
  return hasLocalStorage() ? localStorage.getItem(REFRESH_STORAGE_KEY) : null;
}

export function setRefreshToken(token) {
  if (!hasLocalStorage()) return;
  if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
  else localStorage.removeItem(REFRESH_STORAGE_KEY);
}

export function clearAuthToken() {
  if (!hasLocalStorage()) return;
  localStorage.removeItem(AUTH_STORAGE_KEY);
  // Laisser le jeton de renouvellement derrière soi permettrait de rouvrir une
  // session apres une deconnexion explicite.
  localStorage.removeItem(REFRESH_STORAGE_KEY);
}

export function getAuthHeaders() {
  const key = getAuthToken();
  if (!key) return {};
  return { Authorization: `Bearer ${key}`, 'X-API-Key': key };
}

/**
 * Ajoute le token en query param à une URL SSE.
 * EventSource ne peut pas envoyer d'en-tête Authorization ; le backend accepte
 * donc ?access_token= sur les chemins SSE (cf. app/middleware/auth_gate.py).
 * Sans token (mode local sans auth), l'URL est renvoyée inchangée.
 */
export function withSseToken(url) {
  const key = getAuthToken();
  if (!key) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}access_token=${encodeURIComponent(key)}`;
}

/**
 * Signale globalement qu'une requête a reçu un 401. L'AuthProvider écoute cet
 * événement pour déconnecter proprement et rediriger vers la page de connexion,
 * sans que chaque appelant ait à gérer la redirection.
 */
export function notifyUnauthorized() {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

/** S'abonner aux 401 globaux. Retourne une fonction de désabonnement. */
export function onUnauthorized(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(UNAUTHORIZED_EVENT, handler);
  return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
}
